import React from "react";
import BaseWidget, {WidgetState} from "../../../widgets/BaseWidget";
import {WidgetType} from "../../../constants/WidgetConstants";
import {ValidationTypes} from "../../../constants/WidgetValidation";
import {isAutoLayout} from "utils/autoLayout/flexWidgetUtils";
import {Alignment} from "@blueprintjs/core";
import {LabelPosition, SelectType} from "components/constants";
import {EventType, type ExecutionResult} from "constants/AppsmithActionConstants/ActionConstants";
import {type AutocompletionDefinitions, GRID_DENSITY_MIGRATION_V1} from "widgets/constants";
import panelConfig from "./propertyConfig/PanelConfig";
import {getStyleConfig} from "./propertyConfig/styleConfig";

import {isAutoHeightEnabledForWidget} from "widgets/WidgetUtils";
import type {DerivedPropertiesMap} from "utils/WidgetFactory";
import {Stylesheet} from "../../../entities/AppTheming";
import _ from "lodash";
import EditableTableComponent, {ITEM_OPERATION} from "../component";
import {ExtraDef} from "../../../utils/autocomplete/dataTreeTypeDefCreator";
import { SERVICE_BATCH_DATA } from "ce/sagas/ActionExecution/SetWidgetValue";
import {__DEP_CFG__} from "../../../workers/common/DependencyMap/configUtils";

class EditableTableWidget extends BaseWidget<any, WidgetState> {
  constructor(props: any) {
    super(props);
  }

  static getPropertyPaneEventConfig() {
    return super.getWidgetEvents('EditableTable');
  }

  static getPropertyPaneContentConfig() {
    return [
      {
        sectionName: "标签",
        children: [
          {
            helpText: "Sets the label text of the widget",
            propertyName: "label",
            label: "文本",
            controlType: "INPUT_TEXT",
            placeholderText: "Enter label text",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            helpText: "Sets the label position of the widget",
            propertyName: "labelPosition",
            label: "位置",
            controlType: "ICON_TABS",
            fullWidth: true,
            hidden: isAutoLayout,
            options: [
              {label: "自动", value: LabelPosition.Auto},
              {label: "居左", value: LabelPosition.Left},
              {label: "居上", value: LabelPosition.Top},
            ],
            defaultValue: LabelPosition.Top,
            isBindProperty: false,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            helpText: "Sets the label alignment of the widget",
            propertyName: "labelAlignment",
            label: "对齐方式",
            controlType: "LABEL_ALIGNMENT_OPTIONS",
            fullWidth: false,
            options: [
              {
                startIcon: "align-left",
                value: Alignment.LEFT,
              },
              {
                startIcon: "align-right",
                value: Alignment.RIGHT,
              },
            ],
            isBindProperty: false,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
            hidden: (props: any) =>
              props.labelPosition !== LabelPosition.Left,
            dependencies: ["labelPosition"],
          },
          {
            helpText:
              "Sets the label width of the widget as the number of columns",
            propertyName: "labelWidth",
            label: "宽度（以列为单位）",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
        ],
      },
      {
        sectionName: "表格列",
        children: [
          {
            propertyName: "tableColumns",
            label: "",
            controlType: "TABLE_COLUMNS",
            isBindProperty: true,
            isTriggerProperty: true,
            dependencies: ['tableData'],
            panelConfig: panelConfig as any
          }
        ]
      },
      {
        sectionName: "操作列",
        children: [{
          helpText: "Bind data from an API using {{}}",
          propertyName: "tableAction",
          label: "列",
          controlType: "TABLE_COLUMNS",
          isBindProperty: true,
          isTriggerProperty: false,
          panelConfig: panelConfig as any,
          dependencies: ['dynamicPathExtra'],
          itemDeleteHook: (props: any, item: any) => {
            let keyPrefix = `${props.widgetProperties.widgetName}.tableAction.${item.id}`;
            let _dynamicPathExtra = props.widgetProperties.dynamicPathExtra || [];
            let dynamicPathExtra: any = [];
            _dynamicPathExtra.forEach((item: any) => {
              if (item.indexOf(keyPrefix) == -1) {
                dynamicPathExtra.push(item);
              }
            });
            return {
              dynamicPathExtra,
            }
          },
        },
          {
            propertyName: "isActionCollapse",
            label: "是否折叠按钮",
            helpText: "Controls the visibility of the tab",
            controlType: "SWITCH",
            useValidationMessage: true,
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.BOOLEAN},
          },
          {
            helpText: "超出数量的按钮通过点击折叠图标展示",
            propertyName: "tableActionNo",
            label: "按钮可见数",
            controlType: "INPUT_TEXT",
            placeholderText: '3',
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              }
            },
            hidden: (props: any) => {
              return !props.isActionCollapse;
            },
            dependencies: ["isActionCollapse"]
          },
          {
            propertyName: "actionCollapseIconColor",
            label: "图标颜色",
            helpText: "Changes the background color of the cell",
            controlType: "PRIMARY_COLUMNS_COLOR_PICKER_V2",
            isJSConvertible: true,
            isBindProperty: true,
            validation: {
              type: ValidationTypes.ARRAY_OF_TYPE_OR_TYPE,
              params: {
                type: ValidationTypes.TEXT,
                params: {
                  regex: /^(?![<|{{]).+/,
                },
              },
            },
            isTriggerProperty: false,
            hidden: (props: any) => {
              return !props.isActionCollapse;
            },
            dependencies: ["isActionCollapse"]
          },
          {
            propertyName: "isActionFixed",
            label: "是否冻结",
            helpText: "isFixed",
            controlType: "SWITCH",
            useValidationMessage: true,
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.BOOLEAN},
          }
        ]
      },
      {
        sectionName: "添加新行",
        children: [
          {
            helpText: "Controls the visibility of the widget",
            propertyName: "addNewBtnVisible",
            label: "添加行按钮是否可见",
            controlType: "SWITCH",
            isJSConvertible: false,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.BOOLEAN},
          },
          {
            propertyName: "newLinePosition",
            helpText: "Selects row height",
            label: "新行添加到",
            controlType: "ICON_TABS",
            fullWidth: true,
            defaultValue: "bottom",
            isBindProperty: true,
            isTriggerProperty: false,
            options: [
              {
                label: "顶部",
                value: "top",
              },
              {
                label: "底部",
                value: "bottom",
              },
            ],
          }
        ],
      },
      {
        sectionName: "行选择器",
        children: [
          {
            propertyName: "isRowSelection",
            label: "启用行选择",
            helpText: "Controls the visibility of the tab",
            controlType: "SWITCH",
            useValidationMessage: true,
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            helpText: "Sets the label position of the widget",
            propertyName: "selectType",
            label: "选择类型",
            controlType: "ICON_TABS",
            fullWidth: false,
            options: [
              { label: "多选", value: SelectType.Multiple },
              { label: "单选", value: SelectType.Radio }
            ],
            isBindProperty: false,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
        ]
      },
      {
        sectionName: "验证",
        children: [
          {
            propertyName: "isRequired",
            label: "是否必填",
            helpText: "Makes input to the widget mandatory",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.BOOLEAN},
          },
          {
            helpText:
              "Sets the label width of the widget as the number of columns",
            propertyName: "maxCount",
            label: "最多可添加行数",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
        ],
      },
      {
        sectionName: "分页",
        hidden: (props: any) => props.isTree,
        children: [
          {
            propertyName: "isPagination",
            label: "启用分页",
            helpText: "Controls the visibility of the tab",
            controlType: "SWITCH",
            useValidationMessage: true,
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            helpText: "",
            propertyName: "pageSizeOptions",
            label: "每页多少条数据",
            controlType: "INPUT_TEXT",
            placeholderText: '10,20,50',
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
            hidden: (props: any) => {
              return !props.isPagination;
            },
            dependencies: ["isPagination"]
          }
        ]
      },
      {
        sectionName: "一般",
        children: [
          {
            helpText: "Show help text or details about current selection",
            propertyName: "labelTooltip",
            label: "提示",
            controlType: "INPUT_TEXT",
            placeholderText: "",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            helpText: "Controls the visibility of the widget",
            propertyName: "isVisible",
            label: "是否可见",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.BOOLEAN},
          },
          {
            propertyName: "isDisabled",
            label: "是否禁用",
            helpText: "Disables input to this widget",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.BOOLEAN},
          },
        ],
      },
    ];
  }

  static getPropertyPaneStyleConfig() {
    return getStyleConfig();
  }

  static getDerivedPropertiesMap(): DerivedPropertiesMap {
    return {
      currentItem: "{{this.currentItem}}",
      getSelectedRow: "{{this.currentItem}}",
      getSelectId: "{{this.currentItem.id}}",
      getSelectedRows: "{{this.selectedRows}}",
      getSelectIds: "{{this.selectedRowKeys}}",
      selectId: "{{this.currentItem.id}}",
      selectIds: "{{this.selectedRowKeys}}",
      getValue: `{{ this.value }}`,
      // 判断是否有未保存的记录
      //isValid: `{{this.isRequired ? this.value && this.value.length>0&&this.isSaveComplete==true: true}}`,
      isValid: __DEP_CFG__,
    };
  }

  static getMetaPropertiesMap(): Record<string, any> {
    return {
      value: '',
      isFirst: false,
      currentItem: {},
    };
  }

  static getDefaultPropertiesMap(): Record<string, string> {
    return {
      value: "defaultValue"
    };
  }

  static getAutocompleteDefinitions(): AutocompletionDefinitions {
    return (widget: any, extraDefsToDefine?: ExtraDef) => {
      return {
        // editForm: generateTypeDef(widget.editForm),
      };
    };
  }

  static getStylesheetConfig(): Stylesheet {
    return {
      borderRadius: "{{appsmith.theme.borderRadius.appBorderRadius}}",
      boxShadow: "{{appsmith.theme.boxShadow.appBoxShadow}}",
      labelTextColor: "{{appsmith.theme.form.label.fontColor}}",
      labelTextSize: "{{appsmith.theme.form.label.fontSize}}",
      labelStyle: "{{appsmith.theme.form.label.fontStyle}}",
      accentColor: "{{appsmith.theme.colors.primaryColor}}",
      opBtnBgColor: "{{appsmith.theme.colors.primaryColor}}",
      opBtnColor: "{{appsmith.theme.colors.primaryColor}}",
      newBtnColor: "{{appsmith.theme.colors.primaryColor}}",
      newBtnBgColor: "{{appsmith.theme.colors.primaryColor}}",
      opBtnType: "SECONDARY",
      newBtnType: "TERTIARY",
      primaryColor: "{{appsmith.theme.colors.primaryColor}}",
      backgroundColor: "{{appsmith.theme.colors.backgroundColor}}",
    };
  }

  upTableColumnsMap = (upRecord:any={})=>{
    this.props.updateWidgetMetaProperty("tableColumnsMap", {
      ...this.props.tableColumnsMap,
      ...upRecord
    });
  }

  getTableColumnsMap = () => {
    const {tableColumns, columnSelectOptionsServiceData, tableData, widgetName, editKey} = this.props;
    let columnsMap:Record<string, any> = {};

    const select = _.filter(_.values(tableColumns), { columnType: 'select'});
    _.each(select, i=> {
      let selectKey = `${widgetName}.${i.id}`;
      let selectLabel = `${selectKey}.label`,
        selectValue = `${selectKey}.value`;
      columnsMap[selectKey] = {};

      let tdValueKey = `${i.id}.value`;
      let tdLabelKey = `${i.id}.label`;
      _.each(tableData, j => {
        if (editKey == (j.key || j.id) && columnSelectOptionsServiceData) { // 服务映射返回数据
          columnsMap[selectKey][j.key || j.id] = columnSelectOptionsServiceData?.[selectValue] || columnSelectOptionsServiceData?.[selectLabel];
          columnsMap[`${selectKey}.allMap`] = columnSelectOptionsServiceData[`${selectValue}.allMap`] || columnSelectOptionsServiceData[`${selectLabel}.allMap`]
        } else {
          let value = j[tdValueKey];
          if (value) {
            columnsMap[selectKey][j.key || j.id] = [{
              [selectLabel]: j[tdLabelKey] || value,
              [selectValue]: value
            }];
          }
        }
      })
    })

    return columnsMap;
  }

  //单元格点击事件
  doOnCellClick = (item: any, eventStr: any) => {
    if (!eventStr) {
      return;
    }

    this.props.updateWidgetMetaProperty("currentItem", item);
    let cloneEventStr: any = _.cloneDeep(eventStr);
    let tableColumns = _.keys(this.props.tableColumns);
    let allRows: Record<string, unknown> = {};
    _.each(tableColumns, (i: string) => {
      allRows[i] = '';
    })
    let rows: Record<string, unknown> = {
      ...allRows,
      ...item
    }

    const fn = (data: any) => {
      _.each(data, (i: any) => {
        if (i.type == 'action' && i.actionConfig && (i.actionConfig.apiParams || i.actionConfig.navigateParams)) {
          _.each(rows, (v: any, k: any) => {
            if (i.actionConfig.apiParams) {
              i.actionConfig.apiParams = i.actionConfig.apiParams.replace(`currentItem.${k}`, `"${v}"`);
            }
            if (i.actionConfig.navigateParams) {
              i.actionConfig.navigateParams = i.actionConfig.navigateParams.replace(`currentItem.${k}`, `"${v}"`);
            }
          })
        }
        if (i.type == 'action' && i.actionConfig.navigateURL) {
          _.each(rows, (v: any, k: any) => {
            i.actionConfig.navigateURL = i.actionConfig.navigateURL.replace(/\s/g, '').replace(`{currentItem.${k}}`, `${v}`);
          })

        }
        i.children && fn(i.children);
      })
    };
    if (_.isString(cloneEventStr)) {
      _.each(rows, (v: any, k: any) => {
        cloneEventStr = cloneEventStr
          .replace(`/{currentItem.${k}}`, `/${v}`)
          .replace(`{currentItem.${k}}`, `'${v}'`)
          .replace(`currentItem.${k}`, `'${v}'`);
      })
    } else {
      fn(cloneEventStr.children);
    }

    setTimeout(() => {
      super.executeAction({
        triggerPropertyName: "onClick",
        dynamicString: cloneEventStr,
        event: {
          type: EventType.ON_CLICK,
        },
      });
    }, 100);
  }

  //单元格点击事件
  onCellClick = (item: any, eventStr: any, btnOnClick?: any) => {
    if (btnOnClick && typeof btnOnClick == 'function') {
      btnOnClick(item, this.doOnCellClick, eventStr);
      return;
    }

    this.doOnCellClick(item, eventStr);
  }

  //currPage和pageSize 发生变化时
  paginationChange = (currPage:any, pageSize:any)=>{
    if (!this.props.isDirty) {
      this.props.updateWidgetMetaProperty("isDirty", true);
    }
    this.props.updateWidgetMetaProperty('currPage', currPage);
    this.props.updateWidgetMetaProperty('pageSize', pageSize);
    let _onLoad = this.props.onLoad;
    super.executeAction({
      triggerPropertyName: "onPaginationChange",
      dynamicString: _onLoad,
      event: {
        type: EventType.ON_CLICK,
      },
    });
  }

  handleActionComplete = (result: ExecutionResult) => {

  };

  doOnBatchSaveLinkageEvent = () => {
    let _onBatchSave = this.props.onBatchSave;
    _onBatchSave && super.executeAction({
      triggerPropertyName: "onBatchSave",
      dynamicString: _onBatchSave,
      event: {
        type: EventType.ON_CLICK,
        callback: this.handleActionComplete,
      },
      globalContext: {
      }
    });
  }

  subCellEvents = (eventObj: any, path: string) => {
    super.executeAction({
      triggerPropertyName: "onDropdownOpen",
      dynamicString: eventObj,
      event: {
        type: EventType.ON_DROPDOWN_OPEN
      },
      globalContext: {
        eventType: 'EDITABLE_TABLE_WIDGET.onDropdownOpen'
      }
    });
  }

  doOnBatchDeleteLinkageEvent = () => {
    let _onBatchDelete = this.props.onBatchDelete;
    _onBatchDelete && super.executeAction({
      triggerPropertyName: "onBatchDelete",
      dynamicString: _onBatchDelete,
      event: {
        type: EventType.ON_CLICK,
      },
      globalContext: {
        eventType: 'EDITABLE_TABLE_WIDGET.onBatchDelete'
      }
    });
  }

  //行选择change事件
  onRowSelection = (selectedRowKeys:any, selectedRows:any, selectedIndex:number[]) =>{
    const {pushBatchMetaUpdates, commitBatchMetaUpdates} = this.props;
    pushBatchMetaUpdates('selectedRowKeys', selectedRowKeys);
    pushBatchMetaUpdates('selectedRows', selectedRows);
    pushBatchMetaUpdates('selectedIndex', selectedIndex);
    commitBatchMetaUpdates();
  }

  getPageView() {
    let _defaultValue = this.props.defaultValue || this.props.value;
    let _pageSize = this.props.pageSize;
    if (!this.props.isDirty){
      _pageSize = 10;
      if (this.props.pageSizeOptions && this.props.pageSizeOptions.split.length > 0) {
        _pageSize = this.props.pageSizeOptions.split(',')[0] ;
      }
    }

    return (
      <>
        <EditableTableComponent
          value={_defaultValue}
          widgetId={this.props.widgetId}
          key={this.props.widgetId}
          {...this.props}
          isDynamicHeightEnabled={isAutoHeightEnabledForWidget(this.props as any)}
          labelWidth={this.getLabelWidth()}
          compactMode={!((this.props.bottomRow - this.props.topRow) / GRID_DENSITY_MIGRATION_V1 > 1)}
          onChange={this.onChange}
          onValueChange={this.onValueChange}
          onFormValuesChange={this.onFormValuesChange}
          onOptionChange={this.onOptionChange}
          onSaveCompleteChange={this.onSaveCompleteChange}
          onSetEditKey={this.onSetEditKey}
          subCellEvents={this.subCellEvents}
          onCellClick={this.onCellClick}
          paginationChange={this.paginationChange}
          pageSize={_pageSize}
          onRowSelection={this.onRowSelection}
          doOnBatchSaveLinkageEvent={this.doOnBatchSaveLinkageEvent}
          doOnBatchDeleteLinkageEvent={this.doOnBatchDeleteLinkageEvent}
          tableColumnsMap={this.getTableColumnsMap()}
          upTableColumnsMap={this.upTableColumnsMap}
        />
      </>
    )
  }

  onSetEditKey = (key:any) => {
    this.props.updateWidgetMetaProperty("editKey", key);
  }

  onSaveCompleteChange = (val: any) => {
    this.props.updateWidgetMetaProperty("isSaveComplete", val);
  };
  onValueChange = (updatedValue: any) => {
    this.props.updateWidgetMetaProperty("tableData", updatedValue);
  };

  onFormValuesChange = (values:any) => {
    this.props.updateWidgetMetaProperty("formValues", values);
  }

  onOptionChange = (onOptionChangeEvent: any, record: Record<string, unknown> = {}) => {
    this.props.updateWidgetMetaProperty("currentItem", record.currentItem);
    let event = onOptionChangeEvent;
    if (event) {
      super.executeAction({
        triggerPropertyName: "onOptionChange",
        dynamicString: event,
        event: {
          type: EventType.ON_OPTION_CHANGE,
          callback: this.handleActionResult,
        },
        globalContext: {
          params: JSON.stringify(record.params),
          _mark: 'widgetParams'
        }
      });
    }
  };

  handleActionResult = (result: ExecutionResult) => {
  };

  onChange = (val: any) => {
    this.props.updateWidgetMetaProperty(
      "value",
      val,
    );
    if (this.props.onChange) {
      super.executeAction({
        triggerPropertyName: "onChange",
        dynamicString: this.props.onChange,
        event: {
          type: EventType.ON_CHANGE,
          callback: this.handleActionResult,
        },
      });
    }
  };

  componentDidUpdate(prevProps: any) {
    if (this.props.event === 'onBatchSave') {
      let prevTableData = this.props.value;
      if (!prevTableData || prevTableData.length == 0) {
        prevTableData = prevProps.tableData;
      }

      let tableData = this.props.tableData;
      let newTableData:any = [];
      if (tableData && tableData.length > 0
        && tableData[0].hasOwnProperty(SERVICE_BATCH_DATA)) {

        let tableDataByKey = _.keyBy(tableData, 'key');
        let valueByKey = _.keyBy(prevTableData, 'key');
        _.each(prevTableData, (i:any)=>{
          let newItem:any = valueByKey[i.key] || {};
          let operation = newItem['--operation--'];
          let serviceItem:any = tableDataByKey[i.key] || {};
          if (serviceItem && serviceItem[SERVICE_BATCH_DATA]) {
            if (operation === 'create' || operation === 'update') {
              let newItemData = {...newItem, ...serviceItem};
              newTableData.push({...newItemData});
            }
          } else {
            newTableData.push(newItem);
          }
        })
      } else {
        newTableData = prevTableData;
      }

      newTableData = _.cloneDeep(newTableData.filter((item: any) => item[ITEM_OPERATION] !== "delete"));
      _.forEach(newTableData, item => {
        if (item.hasOwnProperty('--operation--')) {
          delete item['--operation--']
        }
        if (item.hasOwnProperty(SERVICE_BATCH_DATA)) {
          delete item[SERVICE_BATCH_DATA]
        }
      })

      this.props.updateWidgetMetaProperty("event", null);
      this.props.updateWidgetMetaProperty("tableData", newTableData);
      this.props.updateWidgetMetaProperty("value", null);
    }
  }

  static getWidgetType(): WidgetType {
    return 'EDITABLE_TABLE_WIDGET';
  }
}

export default EditableTableWidget;
